import { ColumnState } from 'ag-grid-community'
import { todayOffsetToDate } from './shared'

// ガントチャート専用モック画面（ProjectPlanGantt.tsx, functionUuid=
// mock-project-plan-gantt-function-uuid）向けの列設定。
// この画面は通常のProjectPlanNewをそのまま使い、ganttOnlyModeのような専用
// propや専用ヘッダーは持たない。通常画面はUIStateに保存された列設定が
// 無ければ全列表示のデフォルトのままになるが、ガント専用画面は「表示名・
// ステータス・予定開始日・予定終了日・ガントチャート」の5列だけを表示
// したいため、それ以外の全列を明示的に hide: true にしたColumnStateを
// UIStateの保存値として返す（画面側には列を絞り込む実装を追加しない）。
//
// 注意: HIDDEN_COL_IDS はdefaultColumns.tsの列定義と手動同期が必要（列が
// 追加された場合はここにも追記すること）。また、列設定はUIStateの一度
// きりの適用（applyColumnState）で実現しているため、通常ヘッダーの列設定
// ポップアップから「初期化」操作（gridApi.resetColumnState）を行うと
// 全列が復活する（この画面固有の制約ではなく、列設定ポップアップを持つ
// 画面全般に言えることだが、デモの見た目を壊しうる操作なので明記する）。
export const GANTT_ONLY_MOCK_FUNCTION_UUID =
  'mock-project-plan-gantt-function-uuid'

const VISIBLE_COL_IDS = [
  'body.wbsItem.status',
  'body.wbsItem.scheduledDate.startDate',
  'body.wbsItem.scheduledDate.endDate',
  'ganttChart',
]

const HIDDEN_COL_IDS = [
  'uuid',
  'rowNumber',
  'action',
  'body.wbsItem.type',
  'body.wbsItem.ticketType',
  'body.wbsItem.substatus',
  'body.deliverableAttachmentSummary',
  'body.wbsItem.priority',
  'body.wbsItem.difficulty',
  'body.wbsItem.description',
  'body.wbsItem.team',
  'body.wbsItem.sprint',
  'body.wbsItem.sprints',
  'body.wbsItem.tags',
  'body.wbsItem.watchers',
  'body.wbsItem.estimatedHour',
  'body.wbsItem.estimatedHour.deliverable',
  'body.wbsItem.accountable',
  'body.wbsItem.responsible',
  'body.wbsItem.assignee',
  'startDelayDays',
  'endDelayDays',
  'body.wbsItem.estimatedAmount.deliverable',
  'body.wbsItem.estimatedAmount.task',
  'body.wbsItem.actualAmount',
  'completionAmountRate',
  'body.wbsItem.progressRate',
  'progress.scheduledProgressRate',
  'progress.progressRate',
  'progress.total',
  'progress.scheduledToBe',
  'progress.completed',
  'progress.preceding',
  'progress.delayed',
  'progress.remaining',
  'evm.costVariance',
  'evm.costPerformanceIndex',
  'evm.scheduleVariance',
  'evm.schedulePerformanceIndex',
  'evm.estimateToComplete',
  'evm.estimateAtCompletion',
  'evm.varianceAtCompletion',
  'body.wbsItem.revision',
  'body.wbsItem.createdBy',
  'body.wbsItem.createdAt',
  'body.wbsItem.updatedBy',
  'body.wbsItem.updatedAt',
]

export const ganttOnlyModeColumnState: ColumnState[] = [
  ...VISIBLE_COL_IDS.map(colId => ({ colId, hide: false })),
  ...HIDDEN_COL_IDS.map(colId => ({ colId, hide: true })),
]

// カンバンモック画面（ProjectKanban.tsx）向けの保存済み検索条件。
// KanbanV2は保存済み検索条件が無いと hasInitialCondition が false になり、
// 初期検索をスキップ（skipInitialSearch）したうえで検索条件Popperを開いた
// 状態で描画する（KanbanV2/index.tsx）。デモでは一度検索操作を挟まずに
// カードが並んだ状態を見せたいため、保存済み条件がある体でUIStateを返す。
//
// 条件は予定終了日が現在日付の前後1週間。デモのWBSは開くたびに今日を基準に
// 日付が組み直される（shared.ts）ため、固定日付ではなく相対で算出しないと
// 時間経過でカードが出なくなる。
// dayOffsetToDateではなくtodayOffsetToDateを使うのは、前者がday 0の曜日
// （水曜）に揃えたデモ日付軸を返し、実際の今日と最大6日ずれるため。
//
// rootUuidは使えない。復元経路の互換アダプタ
// （adaptSearchConditionBefore20230125）が、searchFilterを持たない
// 保存形式からrootUuidを必ず取り除くため、rootUuidだけを返しても
// 復元結果が空になり条件なし扱い（Popperが開いた状態）に戻ってしまう。
export const KANBAN_MOCK_FUNCTION_UUID = 'mock-project-kanban-function-uuid'

const KANBAN_SEARCH_RANGE_DAYS = 7

export const kanbanDefaultSearchCondition = {
  scheduledEndDate: {
    from: todayOffsetToDate(-KANBAN_SEARCH_RANGE_DAYS),
    to: todayOffsetToDate(KANBAN_SEARCH_RANGE_DAYS),
  },
}

// 検索条件は列・フィルタ状態（BULK_SHEET_STATE-*）と同じfunctionUuidで
// 引かれるため、keyの接頭辞で見分ける。
// wbsItemSearchConditionStorageServiceが
// `BULK_SHEET_SEARCH_CONDITION_STATE-<projectUuid>` の形で読み書きする。
const SEARCH_CONDITION_UI_STATE_KEY_PREFIX =
  'BULK_SHEET_SEARCH_CONDITION_STATE-'

export const isSearchConditionUiStateKey = (key: string | null): boolean =>
  !!key?.startsWith(SEARCH_CONDITION_UI_STATE_KEY_PREFIX)
